import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import LeadRegistrationPage from "./pages/LeadRegistrationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailsPage from "./pages/ProductDetailsPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import CartPage from "./pages/CartPage";
import CustomerOrdersPage from "./pages/CustomerOrdersPage";
import AdminDashboard from "./pages/AdminDashboard";
import SalesDashboard from "./pages/SalesDashboard";
import LeadsDashboard from "./pages/LeadsDashboard";
import OrdersDashboard from "./pages/OrdersDashboard";
import TelecallerDashboard from "./pages/TelecallerDashboard";
import ThankYouPage from "./pages/ThankYouPage";
import CalendlyDashboard from "./pages/CalendlyDashboard";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import DebugPage from "./pages/DebugPage";
import ReturnRefund from "./pages/ReturnRefund";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsCondition from "./pages/TermsCondition";


// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import CampaignHandler from "./components/CampaignHandler";
import ReferralHandler from "./components/ReferralHandler";

// Context
import { AuthProvider } from "./contexts/AuthContext";
import { MenuAccessProvider } from "./contexts/MenuAccessContext";

// Utils
import { initializeLeadTracking } from "./utils/leadTracking";

// Styles
import "./index.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Initialize lead tracking when app loads
  useEffect(() => {
    initializeLeadTracking();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MenuAccessProvider>
          <Router>
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Navbar />

              <main className="flex-grow">
                <Routes>
                  {/* Campaign and Tracking Routes */}
                  <Route path="/campaign" element={<CampaignHandler />} />
                  <Route path="/ref/:code" element={<ReferralHandler />} />
                  <Route path="/referral" element={<ReferralHandler />} />

                  {/* Public Routes */}
                  <Route path="/" element={<ProductsPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<LeadRegistrationPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route
                    path="/products/:id"
                    element={<ProductDetailsPage />}
                  />
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/thank-you" element={<ThankYouPage />} />

                  {/* Protected User Profile Routes - All authenticated users */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Customer-only Routes */}
                  <Route
                    path="/my-orders"
                    element={
                      <ProtectedRoute requiredRole="customer">
                        <CustomerOrdersPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Dashboard Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sales-dashboard"
                    element={
                      <ProtectedRoute requiredRole="sales">
                        <SalesDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leads-dashboard"
                    element={
                      <ProtectedRoute requiredRole="leads">
                        <LeadsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders-dashboard"
                    element={
                      <ProtectedRoute requiredRole="orders">
                        <OrdersDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/telecaller-dashboard"
                    element={
                      <ProtectedRoute requiredRole="telecaller">
                        <TelecallerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calendly-dashboard"
                    element={<CalendlyDashboard />}
                  />
                  <Route path="/debug" element={<DebugPage />} />
                  <Route path="/return-refund-policy" element={<ReturnRefund />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-condition" element={<TermsCondition />} />

                </Routes>
              </main>

              
              <Footer />

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: "#22c55e",
                      secondary: "#fff",
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: "#ef4444",
                      secondary: "#fff",
                    },
                  },
                }}
              />
            </div>
          </Router>
        </MenuAccessProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
